import {
  createRevealHandover,
  type IRevealHandover,
  REVEAL_HANDOVER_MS
} from "../../../shared/utility/reveal-handover";
import { ConfigScope } from "./config-manager-model";

/**
 * What the Config Manager landing should light up after the editor saves.
 *
 * The editor and the landing are never on screen together — `AppSettingsCanvas` and
 * `GlobalConfigsCanvas` draw one OR the other — so the save that closes the editor unmounts it
 * and mounts the landing fresh. The hand-over therefore works both ways: a landing that is
 * already mounted listens and takes a request at once; a landing about to mount takes the one left
 * for it.
 *
 * A request left for a landing is only good for a moment: the close that wrote it is about to show
 * the landing. One that finds no landing within `CONFIG_REVEAL_HANDOVER_MS` (the save answered a
 * pending navigation and closed onto some other screen) is dropped, so a later visit to the Config
 * Manager does not light an old row.
 *
 * It names the row rather than carrying its id: a create's POST answers with no body, so the saver
 * knows the name it wrote but not the id the row will be listed under. A name is unique among the
 * rows a save can produce — `validateConfigName` holds it unique within the surface's own
 * configurations — and the scope keeps a workspace save from lighting a project row of the same name.
 */
export interface IConfigRevealRequest {
  /** Which landing the row belongs on: the project's Config Manager or the workspace's list. */
  scope: ConfigScope;
  /** The configuration's name as it was saved — what its row shows. */
  name: string;
}

/** How long a request waits for a landing to mount before it is dropped. */
export const CONFIG_REVEAL_HANDOVER_MS: number = REVEAL_HANDOVER_MS;

const handover: IRevealHandover<IConfigRevealRequest> =
  createRevealHandover<IConfigRevealRequest>(CONFIG_REVEAL_HANDOVER_MS);

/** The saver has succeeded and is about to close. */
export const requestConfigReveal: (request: IConfigRevealRequest) => void = handover.request;

/** The landing is mounting. Returns the request left for it, once, if it is still fresh. */
export const takeConfigReveal: () => IConfigRevealRequest | null = handover.take;

/**
 * The landing is on screen: requests reach it directly from now on. Returns the unsubscribe for
 * `componentWillUnmount`.
 */
export const subscribeConfigReveal: (onReveal: (request: IConfigRevealRequest) => void) => () => void =
  handover.subscribe;
